import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mobileSendResetPasswordService } from '../services/mobileSendResetPasswordService';
import { useApiError } from '../../shared/hooks/useApiError';
import { MessageDialog } from '../../shared/ui';

// CSS-in-JS スタイル
const styles = {
  container: {
    minHeight: '100vh',
    background: 'white',
    position: 'relative',
    paddingBottom: '60px'
  },
  title: {
    margin: '60px 10px 40px 10px',
    padding: '6px 0 6px 20px',
    background: '#FDEFEF',
    borderLeft: '6px solid #2B2222'
  },
  titleText: {
    fontSize: '14pt',
    fontWeight: 'bold',
    color: '#2B2222'
  },
  inputField: {
    width: '95%',
    display: 'block',
    margin: '14px auto',
    padding: '10px',
    border: '1px solid',
    borderColor: '#ddd',
    borderRadius: '4px',
    fontSize: '16px',
    background: '#ffffff'
  },
  inputFieldFocus: {
    outline: 'none',
    borderColor: '#2B2222',
    background: 'white'
  },
  inputFieldError: {
    borderColor: '#ff6b6b',
    background: '#fff5f5'
  },
  errorMessage: {
    color: '#ff6b6b',
    textAlign: 'center',
    margin: '10px 0',
    fontSize: '14px'
  },
  message: {
    padding: '10px 20px',
    textAlign: 'left',
    lineHeight: 1.6,
    color: '#333',
    margin: '20px 0'
  },
  sendButton: {
    width: '90%',
    display: 'block',
    margin: '40px auto',
    padding: '12px 0',
    background: '#2B2222',
    color: '#ffffff',
    border: 'none',
    borderRadius: '24px',
    fontSize: '16px',
    letterSpacing: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  topButton: {
    width: '90%',
    display: 'block',
    margin: '40px auto',
    padding: '12px 0',
    background: '#2B2222',
    color: '#ffffff',
    border: 'none',
    borderRadius: '24px',
    fontSize: '16px',
    letterSpacing: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  },
  buttonHover: {
    background: '#4a4a4a'
  },
  buttonDisabled: {
    background: '#cccccc',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  footerContents: {
    textAlign: 'center',
    position: 'fixed',
    bottom: 0,
    width: '100%',
    height: '40px',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    padding: '0 20px'
  },
  footerText: {
    color: '#666',
    fontSize: '14px'
  },
  loginLink: {
    color: '#4a90e2',
    textDecoration: 'none',
    fontSize: '14px'
  },
  loginLinkHover: {
    textDecoration: 'underline'
  },
  formArea: {
    padding: '0 20px'
  },
  messageArea: {
    padding: '0 20px'
  },
  inputContainer: {
    margin: '20px 0'
  },
  buttonContainer: {
    margin: '40px 0'
  }
};

const AppSendResetPassword = () => {
  const navigate = useNavigate();
  
  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false))
  
  // UI状態
  const [mailAddress, setMailAddress] = useState(''); // メールアドレス
  const [isSubmitted, setIsSubmitted] = useState(false); // 送信済みフラグ
  const [isLoading, setIsLoading] = useState(false); // ローディング状態
  const [error, setError] = useState(''); // エラーメッセージ

  // メールアドレスバリデーション
  const validateMailAddress = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // メールアドレス存在確認処理
  const checkMailAddressExists = async (email) => {
    try {
      const result = await mobileSendResetPasswordService.getPixtagUserCount(email);
      return result.returnCode === '200' && result.userCount === 1;
    } catch (error) {
      handleApiError(new Error('メールアドレスの確認に失敗しました。'));
      return false;
    }
  };

  // パスワードリセットメール送信処理
  const sendResetPassword = async (email) => {
    try {
      const result = await mobileSendResetPasswordService.sendResetPassword(email);
      return result.returnCode === '200';
    } catch (error) {
      return false;
    }
  };

  // 送信処理
  const handleSend = async () => {

    setIsLoading(true);

    if (!mailAddress.trim()) {
      setError('メールアドレスを入力してください。');
      setIsLoading(false);
      return;
    }

    if (!validateMailAddress(mailAddress)) {
      setError('正しいメールアドレスを入力してください。');
      setIsLoading(false);
      return;
    }

    setError('');

    try {
      // メールアドレス存在確認
      const exists = await checkMailAddressExists(mailAddress);
      if (!exists) {
        setError('登録されていないメールアドレスです。');
        setIsLoading(false);
        return;
      }

      // パスワードリセットメール送信
      const sent = await sendResetPassword(mailAddress);
      if (sent) {
        setIsSubmitted(true);
      } else {
        handleApiError(new Error('メールの送信に失敗しました。'));
      }
    } catch (error) {
      handleApiError(new Error('メールの送信に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  };

  // TOPページへ遷移
  const handleGoToTop = () => {
    navigate('/appLogin');
  };

  // ログインページへ遷移
  const handleLogin = () => {
    navigate('/appLogin');
  };

  // フォーム入力画面レンダリング
  const renderFormArea = () => (
    <div style={styles.formArea}>
      <div style={styles.title}>
        <span style={styles.titleText}>パスワード再設定ページの送信</span>
      </div>
      <div style={styles.inputContainer}>
        <input
          type="text"
          value={mailAddress}
          onChange={(e) => setMailAddress(e.target.value)}
          placeholder="登録したメールアドレス"
          autoCapitalize="off"
          style={{
            ...styles.inputField,
            ...(error ? styles.inputFieldError : {})
          }}
          onFocus={(e) => {
            e.target.style.outline = 'none';
            e.target.style.borderColor = '#2B2222';
            e.target.style.background = 'white';
            if (error) setError(''); // エラーメッセージもクリア
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#ddd';
            e.target.style.background = '#ffffff';
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isLoading) {
              handleSend();
            }
          }}
        />
      </div>
      {error && <div style={styles.errorMessage}>{error}</div>}
      <div style={styles.buttonContainer}>
        <button 
          onClick={handleSend} 
          disabled={isLoading}
          style={{
            ...styles.sendButton,
            ...(isLoading ? styles.buttonDisabled : {})
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.target.style.background = '#4a4a4a';
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.target.style.background = '#2B2222';
            }
          }}
        >
          {isLoading ? '送信中...' : '送信'}
        </button>
      </div>
    </div>
  );

  // 送信完了画面レンダリング
  const renderMessageArea = () => (
    <div style={styles.messageArea}>
      <div style={styles.title}>
        <span style={styles.titleText}>送信完了</span>
      </div>
      <div style={styles.message}>
        入力されたメールアドレス宛てにパスワード再設定ページへのリンクを送信しました。このページからパスワードを再設定してください。
      </div>
      <div style={styles.buttonContainer}>
        <button 
          onClick={handleGoToTop} 
          style={styles.topButton}
          onMouseEnter={(e) => {
            e.target.style.background = '#4a4a4a';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#2B2222';
          }}
        >
          TOPへ
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      {!isSubmitted ? renderFormArea() : renderMessageArea()}
      
      <div style={styles.footerContents}>
        <span style={styles.footerText}>すでにアカウントがある場合</span>
        <a 
          href="#" 
          onClick={handleLogin} 
          style={styles.loginLink}
          onMouseEnter={(e) => {
            e.target.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.target.style.textDecoration = 'none';
          }}
        >
          ログイン
        </a>
      </div>

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
      />
    </div>
  );
};

export default AppSendResetPassword;
